import React, { useState } from "react";

import {
  HelpCircleIcon,
  LogOutIcon,
  MicOff,
  MoonIcon,
  ShieldIcon,
  SunIcon,
  Upload,
} from "lucide-react";

import { useTheme } from "@/context/ThemeContext";

import { ConfirmationModal } from "@components/ConfirmationModal";
import { useToastStore } from "@components/PersistentToast/store/toast";
import { RecordingNavWarningModal } from "@components/RecordingDock/RecordingNavWarningModal";
import { useRecordingDock } from "@components/RecordingDock/store/useRecordingDock";
import { Badge } from "@components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";

import { DEVELOPMENT_ENV } from "@constants/common";

import { useAuth } from "@hooks/useAuth";

import { hasSavedRecordings } from "@utils/clearRecordingRecovery";
import { getEnv } from "@utils/env";
import { cn } from "@utils/ui";

import { BuildInfoLine } from "./BuildInfoLine";

const isDev = getEnv("VITE_ENV") === DEVELOPMENT_ENV;

interface UserCommandPopoverProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const UserCommandPopover: React.FC<UserCommandPopoverProps> = ({
  children,
  open,
  onOpenChange,
}) => {
  const { logoutWithNavigation } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();

  // Logging out ends any active recording: warn first, and
  // stop + save the session before the sign-out redirect if the user proceeds.
  const recordingExamineeName = useRecordingDock((s) => s.examineeName);
  const [logoutWarnOpen, setLogoutWarnOpen] = useState(false);
  // A recording not yet accepted for transcription (MDRS-551) — this session's,
  // or one still saved in the browser from earlier: signing out deletes it.
  const [unsentWarnOpen, setUnsentWarnOpen] = useState(false);
  // Same for bundle uploads still in flight (MDRS-550): the upload toast
  // minimises instead of closing exactly while it has work it would lose.
  const [uploadsWarnOpen, setUploadsWarnOpen] = useState(false);

  const handleSelectItem = (action: string) => {
    console.log(`Selected action: ${action}`);
    onOpenChange?.(false);
  };

  const doLogout = () => {
    logoutWithNavigation();
    onOpenChange?.(false);
  };

  const logoutUnlessUploading = () => {
    const toast = useToastStore.getState();
    if (toast.isVisible && toast.minimiseOnDismiss) {
      setUploadsWarnOpen(true);
      return;
    }
    doLogout();
  };

  const onLogout = async () => {
    const { isActive, summarize } = useRecordingDock.getState();
    if (isActive()) {
      setLogoutWarnOpen(true);
      return;
    }
    // After a refresh nothing is in memory, but the browser can still hold an
    // unsent or interrupted recording for any case, which logout deletes.
    if (summarize.status !== "idle" || (await hasSavedRecordings())) {
      setUnsentWarnOpen(true);
      return;
    }
    logoutUnlessUploading();
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      {/* Radix portals this out of the sidebar's DOM subtree, so it doesn't
          inherit the sidebar's local `dark` scope — colors are hardcoded here
          rather than via `dark:` variants. Navy per the design (Claude Design
          "Report Studio UI Update" / Main Pages (Half-Dark).dc.html), whose
          user menu panel is a navy card matching the sidebar chrome. */}
      <PopoverContent
        className="m-2 w-64 space-y-1 border border-white/[0.14] bg-primary px-2.5 py-2 shadow-xl"
        side="right"
        align="end"
      >
        <PopoverButton
          icon={<ShieldIcon className="size-5" />}
          label="Terms & Policy"
          onClick={() => handleSelectItem("terms")}
        />
        <PopoverButton
          icon={<HelpCircleIcon className="size-5" />}
          label="Help"
          onClick={() => handleSelectItem("help")}
        />
        {isDev && (
          <PopoverButton
            icon={
              theme === "dark" ? (
                <SunIcon className="size-5" />
              ) : (
                <MoonIcon className="size-5" />
              )
            }
            label={theme === "dark" ? "Light Mode" : "Dark Mode"}
            badge={
              // The default `secondary` variant (light-blue tint, meant for
              // white backgrounds) washed out against this navy popover —
              // same cyan-on-navy treatment as the sidebar's other chrome
              // accents (chrome-accent).
              <Badge
                variant="secondary"
                className="border-chrome-accent/30 bg-chrome-accent/10 px-3 py-0 text-[0.6rem] text-chrome-accent-ink"
              >
                Dev Mode
              </Badge>
            }
            onClick={toggleTheme}
          />
        )}

        <figure className="my-2 h-[1px] w-full bg-white/10" />

        <PopoverButton
          icon={<LogOutIcon className="size-5" />}
          label="Log Out"
          onClick={() => void onLogout()}
          destructive
        />

        <figure className="my-2 h-[1px] w-full bg-white/10" />

        <BuildInfoLine />
      </PopoverContent>

      <RecordingNavWarningModal
        isOpen={logoutWarnOpen}
        examineeName={recordingExamineeName}
        onStay={() => setLogoutWarnOpen(false)}
        onFinishAndLeave={() => {
          useRecordingDock.getState().controls.stop();
          setLogoutWarnOpen(false);
          logoutUnlessUploading();
        }}
      />

      <ConfirmationModal
        open={unsentWarnOpen}
        onOpenChange={setUnsentWarnOpen}
        type="warning"
        icon={<MicOff className="size-10 text-destructive" strokeWidth={2} />}
        title="Recording not sent"
        description="A recording hasn't been sent for transcription yet. Signing out deletes it from this browser. Stay signed in and open the case's Assessment Scribe to send it, or sign out anyway."
        confirmButtonText="Sign out anyway"
        cancelButtonText="Stay signed in"
        onConfirm={() => {
          setUnsentWarnOpen(false);
          logoutUnlessUploading();
        }}
        onCancel={() => setUnsentWarnOpen(false)}
      />

      <ConfirmationModal
        open={uploadsWarnOpen}
        onOpenChange={setUploadsWarnOpen}
        type="warning"
        icon={<Upload className="size-10 text-destructive" strokeWidth={2} />}
        title="Uploads still in progress"
        description="Signing out stops the bundle uploads that are still running, and their documents won't be saved. Stay signed in until they finish, or sign out anyway."
        confirmButtonText="Sign out anyway"
        cancelButtonText="Stay signed in"
        onConfirm={() => {
          setUploadsWarnOpen(false);
          doLogout();
        }}
        onCancel={() => setUploadsWarnOpen(false)}
      />
    </Popover>
  );
};

type PopoverButtonProps = {
  icon: React.ReactNode;
  label: string;
  badge?: React.ReactNode;
  onClick: () => void;
  /** Log Out gets the design's red hover tint instead of the plain white one. */
  destructive?: boolean;
};
const PopoverButton = ({
  icon,
  label,
  badge,
  onClick,
  destructive,
}: PopoverButtonProps) => {
  // Sized to match the sidebar's own nav items exactly (NavigationLinks):
  // text-xs (matches Button size="sm"), gap-2.5, px-2.5 py-2, rounded-[9px],
  // size-5 icons.
  return (
    <button
      className={cn(
        // Montserrat — same as sidebar nav item labels (design: Typefaces).
        "relative grid w-full grid-cols-[auto_1fr] items-center gap-2.5 rounded-[9px] bg-transparent px-2.5 py-2 font-head",
        "border border-transparent text-left text-xs font-normal leading-[1.1] text-white/90",
        "cursor-pointer whitespace-nowrap transition-colors duration-150",
        "[&_svg]:text-white/60",
        destructive
          ? "hover:bg-[#E23A27]/[0.18] hover:text-[#FF8E7E] hover:[&_svg]:text-[#FF8E7E]"
          : "hover:bg-white/10"
      )}
      onClick={onClick}
    >
      {icon}
      <span className="flex items-center gap-2">
        {label}
        {badge}
      </span>
    </button>
  );
};
